'use client';

import { createContext, useContext, useSyncExternalStore, type ReactNode } from 'react';
import { formatDateLabel } from '@/lib/utils/date-format';
import { londonTodayIso } from '@/lib/on-time/local-time';

/**
 * "Now", in a form React can hydrate.
 *
 * 🚨 A component that reads `Date.now()` while rendering asks the question
 * twice — once on the server, once in the browser — and gets two answers. Most
 * of the time they format the same ("3h" and "3h"); at a boundary they do not,
 * and a text mismatch makes React discard the whole document and draw it again
 * (MUCKA-WEB-5 / MUCKA-WEB-A2). How far apart the two answers are is not up to
 * us: the HTML a phone hydrates can be seconds old on a slow Android, or hours
 * old when the service worker serves the cached copy of a page because the
 * network dropped. The replay that pinned this down was Hunter & Hargreaves on
 * /overview, 24 Sep 2026: a `reload` with `transferSize: 0` — the page came out
 * of a cache — where the server's HTML had no trial banner and the browser's
 * first render had "Your trial ends in 5 days". The day count had ticked over
 * between the two renders, and everything on the page went with it.
 *
 * So the server stamps the moment it rendered (`renderedAt`, from the authed
 * layout), and `useRenderNow` returns exactly that stamp for as long as React
 * is hydrating server HTML, then the real clock. `useSyncExternalStore` is what
 * makes that per-boundary rather than per-app: React itself uses the SERVER
 * snapshot for any tree it is hydrating and the client snapshot for anything
 * mounted fresh, and re-renders with the live value straight after hydration.
 * A module-level "have we hydrated yet?" flag would get a late-streamed
 * Suspense boundary wrong.
 *
 * It ticks once a minute, which is the granularity anything reading it shows.
 */
const RenderedAtCtx = createContext<number | null>(null);

export function RenderClockProvider({ renderedAt, children }: { renderedAt: number | null; children: ReactNode }) {
  return <RenderedAtCtx.Provider value={renderedAt}>{children}</RenderedAtCtx.Provider>;
}

const TICK_MS = 60_000;

let current = 0;
let subscribers = 0;
let timer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  subscribers += 1;
  if (!timer) {
    timer = setInterval(() => {
      current = Date.now();
      listeners.forEach((l) => l());
    }, TICK_MS);
  }
  return () => {
    listeners.delete(onChange);
    subscribers -= 1;
    if (subscribers === 0 && timer) {
      clearInterval(timer);
      timer = null;
      // Nobody is watching, so the cached value would go stale; the next
      // reader takes a fresh one.
      current = 0;
    }
  };
}

/** Stable between calls, as `useSyncExternalStore` requires. */
function getSnapshot(): number {
  if (current === 0) current = Date.now();
  return current;
}

/**
 * Milliseconds since the epoch: the server's render moment while hydrating,
 * the live clock (to the minute) otherwise. Use it for anything a render
 * derives from the current time — relative ages, countdowns, "today".
 */
export function useRenderNow(): number {
  const renderedAt = useContext(RenderedAtCtx);
  return useSyncExternalStore(
    subscribe,
    getSnapshot,
    // Outside the authed shell there is no stamp, which is the old behaviour.
    () => renderedAt ?? getSnapshot(),
  );
}

/**
 * Today's London calendar day ("YYYY-MM-DD"), read through `useRenderNow`.
 *
 * 🚨 "Today" is the clock read that does the most damage, because a page the
 * phone hydrates can be a DAY old: the service worker hands back its cached
 * copy of a route whenever the network fetch fails, and an Android WebView
 * restores a backgrounded tab from its own cache. MUCKA-WEB-AX was /schedule
 * hydrating on 29 Sep with HTML from a build that had last been served on the
 * 28th, so the server wrote one day and the phone another, and React threw the
 * whole diary away to reconcile a date label.
 */
export function useLondonTodayIso(): string {
  return londonTodayIso(new Date(useRenderNow()));
}

/** "Monday, 8 June" for today — the page-header date — hydration-safe. */
export function useTodayLabel(): string {
  return formatDateLabel(useRenderNow());
}

/** Test seam. */
export function resetRenderClockForTests(): void {
  current = 0;
}
